import React, { useMemo } from 'react';
import * as R from 'ramda';
import PropTypes from 'prop-types';
import { Row } from '@app/ui';
import Tooltip from '@app/ui/sports-components/Tooltip';
import RPlogo from './RPLogo';
import {
    getPlacementActive,
    getSponsorActiveAtPlacement,
} from '../utils/getActiveOnboardingInstances';

const RPCard = ({
    placement,
    selectPlacement,
    deletePlacement,
    structureLocked,
}) => {
    // TODO: Modify 'reloImages' for actual relo image property name on endpoint
    const { name: plName, examples: images, reloImages, sponsors } = placement;
    const showClientImage = images && images.length > 0;
    const showReloTag = !showClientImage && reloImages && reloImages.length;

    const src =
        (showClientImage ? images[0].imageUrl : showReloTag && reloImages[0]) ||
        null;

    const orderedSponsors = R.sortBy(R.prop('name'), sponsors);
    const activeOrderedSponsors = [
        ...orderedSponsors.filter(sp => getSponsorActiveAtPlacement(sp)),
        ...orderedSponsors.filter(sp => !getSponsorActiveAtPlacement(sp)),
    ];
    const hasSponsors = activeOrderedSponsors.length > 0;

    const sponsorArray = hasSponsors
        ? activeOrderedSponsors.reduce(
              (acc, sp, i) => {
                  if (i < 9) {
                      if (Math.floor(i / 3) > acc.index) {
                          acc.index = Math.floor(i / 3);
                          acc.arr.push([]);
                      }
                      acc.arr[acc.index].push(sp);
                  }
                  return acc;
              },
              { arr: [[]], index: 0 },
          ).arr
        : [];

    const isPlacementActive = useMemo(
        () => getPlacementActive(placement),
        [placement],
    );

    const inactiveTooltip = !isPlacementActive
        ? `This placement is deactivated and will no longer
be tracked. Please reach out to your CSM to reactivate.`
        : '';

    return (
        <div
            className="gds-placement gds-flex-grid__container -m-b-2"
            onClick={selectPlacement}
        >
            <div className="gds-flex-grid__row">
                {/* <!-- Placement Name & Sponsor Logos --> */}

                <div className="gds-flex-grid__item gds-flex-grid__item--no-gutter gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-2 gds-flex-grid__item--mobile-2">
                    <div
                        className={`gds-card gds-flex-grid__item--full-height ${
                            !isPlacementActive ? 'disabled_background' : ''
                        }`}
                    >
                        <div
                            className="gds-card__block -p-l-3-xl -p-l-2-lg -p-r-0 -p-v-0"
                            style={{ maxHeight: '150px' }}
                        >
                            <Tooltip
                                text={plName}
                                position="bottom-right"
                                style={{ width: '100%' }}
                            >
                                <div className="-p-t-2  ">
                                    <p className="gds-text--body-sm gds-text--bold -ellipsis">
                                        {plName}
                                    </p>
                                </div>
                            </Tooltip>
                            {!hasSponsors && (
                                <p className="gds-text--body-sm gds-text--italic">
                                    0 Sponsors
                                </p>
                            )}
                            {hasSponsors && (
                                <div className="gds-flex-grid__container -m-t-1">
                                    {sponsorArray.map((row, rowIndex) => {
                                        const leftOvers =
                                            sponsors.length > 9
                                                ? sponsors.length - 8
                                                : null;
                                        return (
                                            <Row
                                                gutter={16}
                                                className="-m-b-2"
                                                key={`row_${rowIndex}`}
                                            >
                                                {row.map((sp, i) => (
                                                    <RPlogo
                                                        key={`${sp.name}_${i}`}
                                                        {...{
                                                            sp,
                                                            i,
                                                            leftOvers,
                                                            rowIndex,
                                                            placementActive:
                                                                isPlacementActive,
                                                        }}
                                                    />
                                                ))}
                                            </Row>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* <!-- Placement Image --> */}
                <div className="gds-flex-grid__item gds-flex-grid__item--no-gutter gds-flex-grid__item--desktop-2 gds-flex-grid__item--tablet-2 gds-flex-grid__item--mobile-2">
                    <div className="gds-ranker-card__item">
                        {!src && (
                            <div
                                className="gds-flex gds-flex--justify-center gds-flex--direction-col gds-flex--content-center"
                                style={{
                                    alignItems: 'center',
                                    width: '100%',
                                }}
                            >
                                <i className="fal fa-images" />
                                <span className="gds-text--body-sm gds-text--regular -p-t-1">
                                    No media available
                                </span>
                            </div>
                        )}
                        {src && (
                            <>
                                <img className="gds-image" src={src} />
                                {src && showReloTag && (
                                    <div className="gds-ranker-card__data-wrapper">
                                        <div
                                            className="-color-bg-dk-2"
                                            data-test="relo-image-tag"
                                        >
                                            <p className="gds-text--body-sm gds-text--regular -color-tx-white -p-a-1">
                                                Relo Image
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                    {!structureLocked && isPlacementActive && (
                        <div className="gds-ranker-card__footer">
                            <button
                                type="button"
                                className="gds-circular-button -m-r-2 -m-b-1 gds-circular-button--xs gds-circular-button--danger --rp-trash"
                                onClick={deletePlacement}
                            >
                                <i className="fa fa-fw fa-trash-alt" />
                            </button>
                        </div>
                    )}
                </div>

                {!isPlacementActive && (
                    <div
                        className="gds-tooltip -inactive-placement-tooltip gds-tooltip--default gds-tooltip--top"
                        data-tooltip={inactiveTooltip}
                        style={{
                            width: '100%',
                            height: '100%',
                            position: 'absolute',
                            zIndex: 100,
                        }}
                    />
                )}
            </div>
        </div>
    );
};

RPCard.propTypes = {
    placement: PropTypes.object,
    selectPlacement: PropTypes.func,
    deletePlacement: PropTypes.func,
    structureLocked: PropTypes.bool,
};

export default RPCard;
